'use client';

import { useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import {
  $generateHtmlFromNodes,
  $generateNodesFromDOM,
} from '@lexical/html';
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
} from 'lexical';
import { cn } from '@/lib/utils';
import { lexicalTheme } from './lexical-theme';
import { ToolbarPlugin } from './toolbar-plugin';
import './lexical-editor.css';

// ─── Types ────────────────────────────────────────────────────

interface RichTextEditorProps {
  /** Initial content as plain text or HTML string */
  initialContent?: string;
  /** Called when content changes (returns HTML string) */
  onChange?: (html: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the editor is editable */
  editable?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Minimum height for the editor */
  minHeight?: string;
}

// ─── Plugin to set initial HTML content ───────────────────────

function InitialContentPlugin({
  content,
  editable,
}: {
  content: string;
  editable: boolean;
}) {
  const [editor] = useLexicalComposerContext();
  const hasSetContent = useRef(false);

  useEffect(() => {
    if (!content || hasSetContent.current) return;
    hasSetContent.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      // Check if content looks like HTML
      if (content.includes('<') && content.includes('>')) {
        try {
          const parser = new DOMParser();
          const dom = parser.parseFromString(content, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          if (nodes.length > 0) {
            nodes.forEach((node) => root.append(node));
            return;
          }
        } catch (e) {
          console.warn('[LexicalEditor] Failed to parse HTML:', e);
        }
      }

      // Fall back to plain text: split by newlines into paragraphs
      const lines = content.split('\n');
      for (const line of lines) {
        const paragraph = $createParagraphNode();
        if (line.trim()) {
          paragraph.append($createTextNode(line));
        }
        root.append(paragraph);
      }
    });
  }, [editor, content]);

  // Update editable state
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

// ─── Plugin to emit HTML on change ───────────────────────────
// Uses a ref so the listener is registered once and never re-registered,
// avoiding stale-closure and missed-update issues with inline onChange props.

function HtmlOnChangePlugin({
  onChange,
}: {
  onChange?: (html: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      if (!onChangeRef.current) return;
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChangeRef.current!(html);
      });
    });
  }, [editor]);

  return null;
}

// ─── Placeholder component ───────────────────────────────────

function Placeholder({ text }: { text: string }) {
  return <div className="lexical-placeholder">{text}</div>;
}

// ─── Main component ──────────────────────────────────────────

export function RichTextEditor({
  initialContent = '',
  onChange,
  placeholder = 'Start typing...',
  editable = true,
  className,
  minHeight = '150px',
}: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'AudraEditor',
    theme: lexicalTheme,
    onError: (error: Error) => {
      console.error('[LexicalEditor] Error:', error);
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      CodeHighlightNode,
    ],
    editable,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        className={cn(
          'lexical-editor-container',
          !editable && 'readonly',
          className,
        )}
      >
        {/* Toolbar - only show when editable */}
        {editable && <ToolbarPlugin />}

        {/* Editor area */}
        <div className="lexical-editor-inner" style={{ minHeight }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-root"
                style={{ minHeight }}
              />
            }
            placeholder={editable ? <Placeholder text={placeholder} /> : null}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>

        {/* Plugins – HtmlOnChangePlugin MUST come before InitialContentPlugin
            so that the update listener is registered before initial content
            triggers editor.update(), ensuring onChange fires for all changes. */}
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <HtmlOnChangePlugin onChange={onChange} />
        <InitialContentPlugin content={initialContent} editable={editable} />
      </div>
    </LexicalComposer>
  );
}
